import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import {
  Events,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { UtilityService } from "providers/utility-service/utility-service";
import Utils from "utils/Utilities";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "file-upload",
  templateUrl: "file-upload.html",
})
export class FileUploadComponent implements OrderComponentInterface, OnDestroy {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    const componentField = this.orderComponent.properties[0].name;
    if (this._orderFormState.declined && this.listFiles.length > 0) {
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
      this.listFiles = [];
    }
    this.sameProvider = this._orderFormState.isSameProvider;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("inputFile") inputFile: ElementRef;

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  listFiles = [];
  idFiles: string[] = [];
  numberOfFiles = 0;
  dataTransfer = new DataTransfer();
  showToaster = false;
  _orderFormState: OrderFormState;
  sameProvider = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public utilityService: UtilityService,
    public events: Events,
    public referralsService: ReferralsServiceProvider,
    private navBarService: NavBarServiceProvider,
    private dataService: DocManagementDataService
  ) {
    this.events.subscribe("fileUpload", () => {
      this.uploadFile().then();
    });
  }

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnDestroy(): void {
    this.numberOfFiles = 0;
    this.listFiles = [];
    this.events.unsubscribe("fileUpload");
  }

  ngOnInit(): void {
    this.listFiles = [];
    this.idFiles = [];
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    if (this._orderFormState.declined) {
      return false;
    }
    if (isProfessionalProvider(this._orderFormState.credentialedResource)) {
      if (
        this._orderFormState.approvalType == Order.ApprovalTypeEnum.Written &&
        this.sameProvider
      ) {
        return true;
      } else if (
        !this.sameProvider &&
        this._orderFormState.approvalType === Order.ApprovalTypeEnum.Written
      ) {
        return true;
      }
    } else if (
      this._orderFormState.approvalType == Order.ApprovalTypeEnum.Written
    ) {
      return true;
    }

    this.listFiles = [];
    this.idFiles = [];
    this.numberOfFiles = 0;
    return false;
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  selectFile(e) {
    const { files } = e.target;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size < 10485760) {
        this.listFiles.push(files[i]);
        this.dataTransfer.items.add(e.target.files[i]);
      } else {
        this.showToaster = true;
      }
    }
    if (this.showToaster) {
      this.utilityService.presentCustomErrorToast(
        "Some files exceed the size limit."
      );
      this.showToaster = false;
    }
    e.target.files = this.dataTransfer.files;
  }

  async uploadFile() {
    //ULT-854 when file is deleted it remains as undefined in listFiles array so the array is filtered to keep out all the undefined items
    const internalListFiles = this.listFiles.filter((x) => x !== undefined);
    if (internalListFiles.length != 0) {
      await Promise.all(
        this.listFiles
          .filter((x) => x !== undefined)
          .map(async (file, index) => {
            //ULT-854 count total of files filter all undefined
            const counter = internalListFiles.length;
            let payload = {
              key: this.navBarService.getExternalId(),
              label: Utils.getFileName(file.name),
              serviceName: "PatientChart",
              fileExtension: file.name.slice(file.name.lastIndexOf(".")),
              description: "Order File",
              category: "Provider Orders",
              patientID: this.navBarService.getExternalId(),
            };
            let body = new FormData();
            body.append("file", file);
            body.append("tags", JSON.stringify(payload));
            await this.dataService.uploadDocument(body).subscribe(
              (data) => {
                this.idFiles.push(data["documentId"].toString());
                this.numberOfFiles++;
                if (this.numberOfFiles == counter) {
                  this.orderCreateRequestEvent.emit({
                    updates: {
                      signatureRequests: {
                        created: {
                          documentIds: Array.from(new Set(this.idFiles)),
                        },
                      },
                    },
                  });
                  this.events.publish("sendForm");
                  this.listFiles = [];
                }
              },
              (err) => {
                this.numberOfFiles++;
                if (this.numberOfFiles == counter) {
                  this.orderCreateRequestEvent.emit({
                    updates: {
                      signatureRequests: {
                        created: {
                          documentIds: Array.from(new Set(this.idFiles)),
                        },
                      },
                    },
                  });
                  this.events.publish("sendForm");
                  this.listFiles = [];
                }
              }
            );
          })
      );
    } else {
      this.events.publish("sendForm");
    }
  }

  deleteFile(event: any, index: number) {
    this.dataTransfer.items.remove(index);
    const newFileList = [];
    this.inputFile.nativeElement.files = this.dataTransfer.files;
    for (let i = 0; i < this.listFiles.length; i++) {
      newFileList.push(this.dataTransfer.files[i]);
    }
    this.listFiles = newFileList;
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/file-upload/file-upload.ts